import { api } from "@/lib/http.ts";
import type { CheckoutResp, PlanChangeReq, PlanResp } from "@/types/billing.ts";
import type { PaymentMethodListResp, SetupIntentResp } from "@/types/wallet.ts";

const BASE_API_PATH = "/api/a/billing";

export const getPaymentMethods = async (): Promise<PaymentMethodListResp> => {
  return api.get<PaymentMethodListResp>(`${BASE_API_PATH}/payment-methods`).then((res) => res.data);
};

export const createSetupIntent = async (): Promise<SetupIntentResp> => {
  const response = await api.post<SetupIntentResp>(`${BASE_API_PATH}/payment-methods/setup-intent`);
  return response.data;
};

/**
 * Re-reads the vault from Stripe. Called right after a card is confirmed so
 * the list reflects the save immediately rather than waiting for Stripe's
 * webhook to land.
 */
export const refreshPaymentMethods = async (): Promise<PaymentMethodListResp> => {
  const response = await api.post<PaymentMethodListResp>(
    `${BASE_API_PATH}/payment-methods/refresh`,
  );
  return response.data;
};

/**
 * Removing the last card while a plan is active needs `confirm` — the server
 * answers the unconfirmed attempt with a 409 so the warning is always shown
 * before billing can break.
 */
export const deletePaymentMethod = async (id: string, confirm = false) => {
  return api.post(`${BASE_API_PATH}/payment-methods/${id}/delete`, { confirm });
};

export const setDefaultPaymentMethod = async (id: string) => {
  return api.post(`${BASE_API_PATH}/payment-methods/${id}/default`);
};

export const getPlan = async (): Promise<PlanResp> => {
  return api.get<PlanResp>(`${BASE_API_PATH}/plan`).then((res) => res.data);
};

/** A paid plan as the user picks it: the tier and how often it bills. */
export type PlanChoice = Required<PlanChangeReq>;

/** Returns Stripe's hosted Checkout URL for the caller to navigate to. */
export const startCheckout = async (choice: PlanChoice): Promise<CheckoutResp> => {
  const response = await api.post<CheckoutResp>(`${BASE_API_PATH}/checkout`, choice);
  return response.data;
};

/**
 * Settles the plan after Stripe sends the user back. The server re-reads the
 * subscription live, so the returned plan is correct even though the webhook
 * may not have landed yet.
 */
export const refreshCheckout = async (sessionId: string): Promise<PlanResp> => {
  const response = await api.post<PlanResp>(`${BASE_API_PATH}/checkout/refresh`, { sessionId });
  return response.data;
};

/**
 * Moves the plan to another tier or billing interval. Changes that give more
 * apply immediately and prorate; changes that give less take effect at the
 * period end (`planChangeTiming`), so the response may show the current plan
 * with a scheduled change rather than the requested one.
 */
export const changePlan = async (choice: PlanChoice): Promise<PlanResp> => {
  const response = await api.post<PlanResp>(`${BASE_API_PATH}/plan/change`, choice);
  return response.data;
};

export const cancelPlan = async (): Promise<PlanResp> => {
  const response = await api.post<PlanResp>(`${BASE_API_PATH}/plan/cancel`);
  return response.data;
};

export const resumePlan = async (): Promise<PlanResp> => {
  const response = await api.post<PlanResp>(`${BASE_API_PATH}/plan/resume`);
  return response.data;
};

/** Drops a change scheduled for the period end, keeping the plan held now. */
export const keepCurrentPlan = async (): Promise<PlanResp> => {
  const response = await api.post<PlanResp>(`${BASE_API_PATH}/plan/scheduled-change/release`);
  return response.data;
};
